import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  Grid,
  Container,
  Typography,
  Stack,
  Box,
  Paper,
  IconButton,
  Button,
  Avatar,
  InputBase,
  Fab,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Pagination,
  Popover,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search,
  SlidersHorizontal,
  Plus,
  Globe,
  Clock,
  MessageCircle,
  Smartphone,
  ShieldCheck,
  CreditCard,
  Building2,
  Settings,
  User as UserIcon,
  LogOut,
  X,
  Zap,
  Users,
  Key,
  Bell,
  LifeBuoy,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'react-toastify';

import Page from '../components/Page';
import ModalC from './components/CModal';
import { api, apiMain } from '../api/config/axios';
import { SET_ACTIVE_APP, CLEAR_APP_DATA } from '../store/constants/auth';
import { userLogOut, restoreUserSession } from '../store/actions/auth';
// Fetch staff invitation and user apps helpers
import { getMyPendingInvitations, acceptStaffInvitation, getUserApps } from '../api/users.api';
import { getSubscriptions } from '../api/subscriptions.api';

// ─── Styled Components ───────────────────────────────────────────────────────

const GlassCard = styled(Paper)(({ theme }) => ({
  borderRadius: 24,
  // Responsive padding: smaller on mobile, standard on desktop
  padding: theme.spacing(2.5),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3.5),
  },
  background: '#ffffff',
  border: '1px solid rgba(0,0,0,0.06)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
    borderColor: 'rgba(0, 113, 227, 0.3)',
  },
}));

const ActionButton = styled(Button)(() => ({
  borderRadius: 12,
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 600,
  fontFamily: '"SF Pro Text", sans-serif',
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  border: '1px solid #e0e0e0',
  borderRadius: 8,
  padding: '4px 12px',
  backgroundColor: '#fff',
  height: 40,
  // Full width on mobile, fixed max-width on larger screens
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 280,
  },
}));

const CreateButton = styled(Button)(() => ({
  borderRadius: 6,
  padding: '8px 20px',
  textTransform: 'none',
  fontWeight: 600,
  backgroundColor: '#3b5ee6',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#2a48c6',
    boxShadow: 'none',
  },
}));

// ─── Dummy Data ──────────────────────────────────────────────────────────────

// ─── Main Component ──────────────────────────────────────────────────────────

export default function DashboardMain() {
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectingAppId, setSelectingAppId] = useState(null);

  // Load search, filter, and pagination states from MainLayout context
  const { searchQuery, filterRole, filterStatus, filterAppType, page, setPage } = useOutletContext();

  const handleSelectApp = async (app, targetPath = '/dashboard/app') => {
    if (selectingAppId) return;
    setSelectingAppId(app._id);

    try {
      try {
        localStorage.removeItem('cached_dashboard_overview');
      } catch (e) {
        // Ignore storage error
      }
      dispatch({ type: CLEAR_APP_DATA });

      api.setBaseURL(app.urls.apiUrl);
      api.setHeader('x-app-id', app._id);
      localStorage.setItem('activeApp', JSON.stringify(app));
      dispatch({ type: SET_ACTIVE_APP, payload: app });

      try {
        const response = await apiMain.post('/api/v1/user/select-app', { appId: app._id });
        if (response.ok && response.data?.success) {
          const { user: appUser } = response.data.data;
          dispatch({ type: 'USER_LOADED', payload: { user: appUser } });
        }
      } catch (err) {
        console.error('Failed to select app:', err);
      }

      navigation(targetPath);
    } finally {
      setSelectingAppId(null);
    }
  };

  const handleCreateApp = () => {
    navigation('/dashboard/create-app');
  };

  const [invitations, setInvitations] = React.useState([]);
  const [loadingInvites, setLoadingInvites] = React.useState(false);

  const [apps, setApps] = React.useState([]);
  const [totalPages, setTotalPages] = React.useState(1);
  const [loadingApps, setLoadingApps] = React.useState(false);

  const fetchInvitations = async () => {
    try {
      const res = await getMyPendingInvitations();
      if (res.ok && res.data?.success) {
        setInvitations(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch pending invitations:', err);
    }
  };

  const fetchApps = async () => {
    setLoadingApps(true);
    try {
      const res = await getUserApps({
        page,
        limit: 10,
        search: searchQuery,
        status: filterStatus,
        appType: filterAppType,
        role: filterRole,
      });
      if (res.ok && res.data?.success) {
        const appsData = res.data.data;
        const appsList = Array.isArray(appsData) ? appsData : appsData?.apps || [];
        setApps(appsList);
        setTotalPages(appsData?.pagination?.pages || 1);
      }
    } catch (err) {
      console.error('Failed to fetch user apps:', err);
    } finally {
      setLoadingApps(false);
    }
  };

  const [expiringSubscriptions, setExpiringSubscriptions] = React.useState([]);

  const fetchExpiringSubscriptions = async () => {
    try {
      const res = await getSubscriptions();
      if (res.ok && res.data?.success) {
        const allSubs = res.data.data || [];
        const expiring = allSubs.filter((sub) => {
          const { daysLeft: subDaysLeft, expiresAt } = sub;
          let daysLeft = 999;
          if (subDaysLeft !== undefined) {
            daysLeft = subDaysLeft;
          } else if (expiresAt) {
            daysLeft = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
          }
          return daysLeft <= 7;
        });
        setExpiringSubscriptions(expiring);
      }
    } catch (err) {
      console.error('Failed to fetch expiring subscriptions:', err);
    }
  };

  React.useEffect(() => {
    fetchInvitations();
    fetchExpiringSubscriptions();
  }, []);

  const isAnyAppOwner = apps.some((app) => {
    const ownerId = typeof app.owner === 'object' ? app.owner?._id || app.owner?.id : app.owner;
    return ownerId === user?._id || ownerId === user?.id;
  });

  React.useEffect(() => {
    fetchApps();
  }, [page, searchQuery, filterStatus, filterAppType, filterRole]);

  const handleAcceptInvite = async (inviteId) => {
    setLoadingInvites(true);
    try {
      const res = await acceptStaffInvitation(inviteId);
      if (res.ok) {
        toast.success('Invitation accepted successfully!');
        dispatch(restoreUserSession());
        fetchInvitations();
        fetchApps();
      } else {
        toast.error(res.data?.message || 'Failed to accept invitation');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setLoadingInvites(false);
    }
  };

  const renderAppsContent = () => {
    if (loadingApps) {
      return Array.from(new Array(8)).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
          <GlassCard sx={{ opacity: 0.7 }}>
            <Stack direction="row" justifyContent="space-between" mb={3}>
              <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: '14px' }} />
              <Skeleton variant="rectangular" width={65} height={24} sx={{ borderRadius: '8px' }} />
            </Stack>
            <Skeleton variant="text" width="80%" height={32} sx={{ mb: 1 }} />
            <Stack spacing={1.5} mt={2}>
              <Skeleton variant="text" width="90%" height={20} />
              <Skeleton variant="text" width="70%" height={20} />
            </Stack>
            <Stack direction="row" spacing={1.5} mt={4}>
              <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: '12px' }} />
              <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: '12px' }} />
            </Stack>
          </GlassCard>
        </Grid>
      ));
    }

    if (apps.length === 0) {
      return (
        <Grid item xs={12}>
          <Paper
            sx={{
              p: { xs: 4, md: 8 },
              textAlign: 'center',
              borderRadius: '24px',
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              maxWidth: 600,
              mx: 'auto',
              mt: 4,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '24px',
                background: 'linear-gradient(135deg, #f5f5f7 0%, #e8e8ed 100%)',
                color: '#86868b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                boxShadow: '0px 10px 20px rgba(0,0,0,0.03)',
              }}
            >
              <Smartphone size={40} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1d1d1f', mb: 1.5 }}>
              No apps found
            </Typography>
            <Typography variant="body1" sx={{ color: '#86868b', mb: 4, maxWidth: 400, lineHeight: 1.6 }}>
              Create your first application workspace or check your filters to get started.
            </Typography>
            <Button
              variant="contained"
              onClick={handleCreateApp}
              startIcon={<Plus size={20} />}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                py: 1.5,
                px: 4,
                fontSize: '1rem',
                fontWeight: 600,
                bgcolor: '#1d1d1f',
                color: 'white',
                '&:hover': { bgcolor: '#2c2c2e' },
              }}
            >
              Create your first app
            </Button>
          </Paper>
        </Grid>
      );
    }

    return apps.map((app) => (
      <Grid item xs={12} sm={6} md={4} lg={3} key={app._id}>
        <GlassCard>
          {/* Card Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
            {app.branding?.logoUrl ? (
              <Avatar
                src={app.branding.logoUrl}
                variant="rounded"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '14px',
                  border: '1px solid rgba(0,0,0,0.06)',
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '14px',
                  bgcolor: `${app.branding?.themeColor || '#3b5ee6'}15`,
                  color: app.branding?.themeColor || '#3b5ee6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Smartphone size={24} strokeWidth={2} />
              </Box>
            )}
            <Chip
              label={app.status.charAt(0).toUpperCase() + app.status.slice(1)}
              size="small"
              sx={{
                bgcolor: app.status === 'active' ? '#e8f5e9' : '#fff4e5',
                color: app.status === 'active' ? '#2e7d32' : '#ed6c02',
                fontWeight: 700,
                fontSize: '0.75rem',
              }}
            />
          </Stack>

          {/* App Info */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h5" noWrap sx={{ fontWeight: 700, color: '#1d1d1f', mb: 0.5 }}>
              {app.name}
            </Typography>

            <Stack spacing={1.5} mt={2}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Globe size={16} color="#86868b" style={{ flexShrink: 0 }} />
                <Typography variant="body2" noWrap sx={{ color: '#424245', fontWeight: 500 }}>
                  {app.urls?.websiteUrl}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <ShieldCheck size={16} color="#86868b" style={{ flexShrink: 0 }} />
                <Typography variant="body2" noWrap sx={{ color: '#424245', fontWeight: 500 }}>
                  {(app.business?.plan || app.config?.plan || 'Starter').charAt(0).toUpperCase() +
                    (app.business?.plan || app.config?.plan || 'Starter').slice(1)}{' '}
                  Plan
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={1.5} mt={4}>
            <ActionButton
              fullWidth
              variant="contained"
              onClick={() => handleSelectApp(app)}
              disabled={app.status !== 'active' || !!selectingAppId}
              sx={{ bgcolor: '#1d1d1f', color: 'white', '&:hover': { bgcolor: '#2c2c2e' } }}
            >
              {selectingAppId === app._id ? <CircularProgress size={20} color="inherit" /> : 'Manage App'}
            </ActionButton>
            <IconButton
              onClick={() => handleSelectApp(app, '/dashboard/billing')}
              disabled={app.status !== 'active' || !!selectingAppId}
              sx={{ border: '1px solid #d2d2d7', borderRadius: '12px', flexShrink: 0 }}
            >
              {selectingAppId === app._id ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CreditCard size={20} color="#1d1d1f" />
              )}
            </IconButton>
          </Stack>
        </GlassCard>
      </Grid>
    ));
  };

  return (
    <Page title="My Applications">
      <Box sx={{ minHeight: '100vh', bgcolor: '#fbfbfd', pb: 10 }}>
        <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 4 } }}>
          {expiringSubscriptions.length > 0 && isAnyAppOwner && (
            <Paper
              sx={{
                p: 3,
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #ff9800 0%, #ed6c02 100%)',
                color: 'white',
                boxShadow: '0px 10px 30px rgba(237, 108, 2, 0.25)',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                mb: 4,
              }}
            >
              <Stack direction="row" spacing={2.5} alignItems="center">
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                  }}
                >
                  <AlertTriangle size={24} />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Subscription Expiration Alert
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.95 }}>
                    You have <strong>{expiringSubscriptions.length}</strong> service
                    {expiringSubscriptions.length > 1 ? 's' : ''} expiring soon or requiring renewal. Renew to maintain
                    uninterrupted uptime.
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                onClick={() => navigation('/dashboard/global-billing')}
                endIcon={<ArrowRight size={18} />}
                sx={{
                  bgcolor: 'white',
                  color: '#d35400',
                  fontWeight: 700,
                  borderRadius: '8px',
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  flexShrink: 0,
                  alignSelf: { xs: 'stretch', md: 'center' },
                  '&:hover': {
                    bgcolor: '#fff5ec',
                  },
                }}
              >
                Go to Billings
              </Button>
            </Paper>
          )}

          {invitations.length > 0 && (
            <Box sx={{ mb: 4 }}>
              {invitations.map((invite) => (
                <Paper
                  key={invite._id}
                  sx={{
                    p: 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #5D5FEF 0%, #4B4DCC 100%)',
                    color: 'white',
                    boxShadow: '0px 10px 30px rgba(93, 95, 239, 0.2)',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Stack direction="row" spacing={2.5} alignItems="center">
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                      }}
                    >
                      <Users size={24} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Teammate Invitation
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        You have been invited to join <strong>{invite.appId?.name || 'an application'}</strong> as a{' '}
                        <strong>{invite.role}</strong>.
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1.5} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>
                    <Button
                      variant="contained"
                      onClick={() => handleAcceptInvite(invite._id)}
                      disabled={loadingInvites}
                      sx={{
                        bgcolor: 'white',
                        color: '#5D5FEF',
                        fontWeight: 700,
                        borderRadius: '8px',
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        '&:hover': {
                          bgcolor: '#f5f5f5',
                        },
                      }}
                    >
                      Accept Invitation
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Box>
          )}

          {/* ─── Apps Grid ─── */}
          <Grid container spacing={4} alignItems="stretch">
            {renderAppsContent()}
          </Grid>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Stack direction="row" justifyContent="center" sx={{ mt: 6 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, val) => setPage(val)}
                color="primary"
                size="large"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    fontWeight: 600,
                  },
                }}
              />
            </Stack>
          )}
        </Container>

        {/* ─── Floating Chat/Support Icon ─── */}
        <Fab
          color="inherit"
          aria-label="support"
          sx={{
            position: 'fixed',
            bottom: { xs: 24, md: 32 },
            right: { xs: 24, md: 32 },
            bgcolor: '#fff',
            color: '#28a745',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }}
        >
          <MessageCircle size={28} />
        </Fab>

        {/* ─── Coming Soon Modal ─── */}
        <ModalC
          isOpen={isModalOpen}
          setOpen={() => setIsModalOpen(false)}
          sx={{ width: { xs: '90vw', sm: 400 }, p: { xs: 4, sm: 5 }, borderRadius: 3 }}
        >
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'rgba(59, 94, 230, 0.1)',
                color: '#3b5ee6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Clock size={32} strokeWidth={2} />
            </Box>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                Coming Soon
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
                The ability to request and deploy a brand new platform directly from this dashboard will be available
                shortly.
              </Typography>
            </Box>

            <CreateButton fullWidth variant="contained" onClick={() => setIsModalOpen(false)} sx={{ mt: 2 }}>
              Close
            </CreateButton>
          </Stack>
        </ModalC>
      </Box>
    </Page>
  );
}
